import {isWeb} from '#/platform/detection'

const COINGECKO_BASE = 'https://api.coingecko.com/api/v3'
const COINGECKO_PROXY_BASE = process.env.EXPO_PUBLIC_COINGECKO_PROXY_URL

const DEFAULT_BACKOFF_MS = 60_000
const backoffByUrl = new Map<string, number>()
const inflight = new Map<string, Promise<unknown>>()
let globalBackoffUntil = 0

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : typeof process !== 'undefined'
      ? process.env.NODE_ENV !== 'production'
      : false

type CoingeckoError = Error & {status?: number}

const buildUrl = (path: string, params?: Record<string, string>) => {
  const trimmed = path.replace(/^\/+/, '')
  const query = params
    ? Object.entries(params)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join('&')
    : ''
  const suffix = query ? `?${query}` : ''
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1')
  const useProxy =
    isWeb && (Boolean(COINGECKO_PROXY_BASE) || isDev || isLocalhost)
  if (useProxy) {
    const base = (COINGECKO_PROXY_BASE || '/__proxy/coingecko').replace(
      /\/+$/,
      '',
    )
    return `${base}/${trimmed}${suffix}`
  }
  return `${COINGECKO_BASE}/${trimmed}${suffix}`
}

const markBackoff = (url: string, ms = DEFAULT_BACKOFF_MS) => {
  const until = Date.now() + ms
  backoffByUrl.set(url, until)
  if (until > globalBackoffUntil) {
    globalBackoffUntil = until
  }
}

const createRateLimitError = () => {
  const error = new Error('coingecko_rate_limited') as CoingeckoError
  error.status = 429
  return error
}

export const isRateLimited = (err: unknown) =>
  (err as CoingeckoError)?.status === 429 ||
  (err as Error)?.message?.includes('rate_limited')

export async function fetchCoingeckoJson<T>(
  path: string,
  params?: Record<string, string>,
) {
  const url = buildUrl(path, params)
  if (globalBackoffUntil && Date.now() < globalBackoffUntil) {
    throw createRateLimitError()
  }
  const backoffUntil = backoffByUrl.get(url)
  if (backoffUntil && Date.now() < backoffUntil) {
    throw createRateLimitError()
  }

  const existing = inflight.get(url)
  if (existing) {
    return (await existing) as T
  }

  const requestPromise = (async () => {
    const response = await fetch(url, {
      method: 'GET',
      headers: {accept: 'application/json'},
    })

    if (response.status === 429) {
      markBackoff(url)
      throw createRateLimitError()
    }

    if (!response.ok) {
      const error = new Error(`coingecko_${response.status}`) as CoingeckoError
      error.status = response.status
      throw error
    }

    return (await response.json()) as T
  })()

  inflight.set(url, requestPromise)
  try {
    return await requestPromise
  } finally {
    inflight.delete(url)
  }
}
